import { strFromU8, unzipSync } from "fflate"
import { Converter } from "showdown"

import {
	applyStepsListStyle,
	stripEmojis,
} from "@productlane/lib/src/help-center-scrape/content-polish"
import {
	assignUniqueIcons,
	buildCardSummary,
} from "@productlane/lib/src/help-center-scrape/polish"

import { unwrapBlockImages } from "@/components/editor/unwrap-block-images"

export interface MarkdownArticle {
	title: string
	content: string
	summary?: string
	imageUrl?: string
	groupName?: string
	published?: boolean
	dateIso?: string
	icon?: string
	filename: string
}

export interface MarkdownFrontmatter {
	title?: string
	summary?: string
	description?: string
	image?: string
	imageUrl?: string
	group?: string
	groupName?: string
	published?: boolean | string
	date?: string
	created?: string
	createdAt?: string
}

/**
 * Convert Markdown to HTML using showdown
 */
function convertMarkdownToHtml(markdown: string): string {
	const converter = new Converter({
		tables: true,
		strikethrough: true,
		tasklists: true,
		openLinksInNewWindow: true,
		backslashEscapesHTMLTags: true,
		// Showdown ends every code block with a newline, which the editor shows as an empty last line.
		omitExtraWLInCodeBlocks: true,
	})

	// Notion exports callouts as raw HTML <aside> blocks that contain markdown.
	// Showdown does not parse markdown inside HTML blocks, so we convert the inner
	// markdown ourselves and keep the <aside> wrapper.
	const withConvertedAsides = markdown.replace(
		/<aside>([\s\S]*?)<\/aside>/gi,
		(_, inner: string) => {
			const withoutNotionIcon = inner.replace(
				/<img[^>]+src=["']https:\/\/www\.notion\.so\/icons\/[^"']+["'][^>]*>\s*/gi,
				"",
			)
			const innerMarkdown = withoutNotionIcon.trim()
			const innerHtml = converter.makeHtml(innerMarkdown)
			return `<aside>${innerHtml}</aside>`
		},
	)

	return unwrapBlockImages(converter.makeHtml(withConvertedAsides))
}

/**
 * Parse markdown content and extract frontmatter and content
 */
export function parseMarkdownFile(
	content: string,
	filename: string,
): MarkdownArticle {
	// Check for frontmatter (YAML between --- markers)
	const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/
	const match = content.match(frontmatterRegex)

	let frontmatter: MarkdownFrontmatter = {}
	let markdownContent = content

	if (match) {
		const frontmatterText = match[1]
		const contentMatch = match[2]

		if (frontmatterText && contentMatch) {
			markdownContent = contentMatch
			// Simple YAML parser for basic key-value pairs
			frontmatter = parseYamlFrontmatter(frontmatterText)
		}
	}

	let bodyMarkdown = markdownContent.replace(/^\s+/, "")
	const leadingHeading = bodyMarkdown.match(/^#[ \t]+(.+?)[ \t]*(?:\n|$)/)
	const leadingTitle = leadingHeading?.[1]?.trim()

	// Title comes from frontmatter, a leading H1, the first heading anywhere, or the filename
	const title = stripEmojis(
		frontmatter.title ||
			leadingTitle ||
			bodyMarkdown.match(/^#[ \t]+(.+)$/m)?.[1]?.trim() ||
			filename.replace(/\.md$/i, "").replace(/[-_]/g, " "),
	)

	// Drop the leading H1 from the body only when it IS the title, so the title
	// isn't rendered twice and we never delete an unrelated heading.
	if (
		leadingHeading &&
		leadingTitle &&
		(!frontmatter.title || frontmatter.title.trim() === leadingTitle)
	) {
		bodyMarkdown = bodyMarkdown.slice(leadingHeading[0].length)
	}

	// An author's own summary is theirs, so it only loses its emoji. A generated
	// one is sized to the docs card instead, so the set reads as one column.
	const providedSummary = frontmatter.summary || frontmatter.description
	const summary = providedSummary
		? stripEmojis(providedSummary)
		: buildCardSummary(undefined, bodyMarkdown)

	// Handle image URL
	const imageUrl = frontmatter.image || frontmatter.imageUrl

	// Handle group name
	const groupName = frontmatter.group || frontmatter.groupName

	// Handle published status
	let published: boolean | undefined
	if (frontmatter.published !== undefined) {
		if (typeof frontmatter.published === "boolean") {
			published = frontmatter.published
		} else {
			const value = frontmatter.published.toString().toLowerCase()
			published =
				value === "true" ||
				value === "yes" ||
				value === "1" ||
				value === "published"
		}
	}

	// Handle date
	const dateIso =
		frontmatter.date || frontmatter.created || frontmatter.createdAt

	return {
		title,
		content: convertMarkdownToHtml(applyStepsListStyle(bodyMarkdown.trim())),
		summary,
		imageUrl,
		groupName,
		published,
		dateIso,
		filename,
	}
}

/**
 * Simple YAML frontmatter parser for basic key-value pairs
 */
function parseYamlFrontmatter(yamlText: string): MarkdownFrontmatter {
	const result: Record<string, string | boolean> = {}
	const lines = yamlText.split("\n")

	for (const line of lines) {
		const trimmed = line.trim()
		if (!trimmed || trimmed.startsWith("#")) continue

		const colonIndex = trimmed.indexOf(":")
		if (colonIndex === -1) continue

		const key = trimmed.substring(0, colonIndex).trim()
		let value = trimmed.substring(colonIndex + 1).trim()

		// Remove quotes if present
		if (
			(value.startsWith('"') && value.endsWith('"')) ||
			(value.startsWith("'") && value.endsWith("'"))
		) {
			value = value.slice(1, -1)
		}

		// Convert boolean strings
		if (value.toLowerCase() === "true") {
			result[key] = true
		} else if (value.toLowerCase() === "false") {
			result[key] = false
		} else {
			result[key] = value
		}
	}

	return result as MarkdownFrontmatter
}

/**
 * Parse a zip file. Folder structure zip -> folder -> files is used for grouping.
 * Deeper nesting is flattened: the first folder segment is always the group.
 */
export interface MarkdownEntry {
	path: string
	contents: string
}

/**
 * Folder structure path -> file is used for grouping. Deeper nesting is
 * flattened: the immediate parent folder is always the group.
 */
export function parseMarkdownEntries(
	entries: MarkdownEntry[],
): MarkdownArticle[] {
	const articles: MarkdownArticle[] = []

	for (const { path, contents } of entries) {
		if (!path.toLowerCase().endsWith(".md")) continue
		if (contents.length === 0) continue

		try {
			const segments = path.split("/").filter(Boolean)
			const filename = segments[segments.length - 1] ?? path
			const groupSegment =
				segments.length >= 2 ? segments[segments.length - 2] : undefined

			const article = parseMarkdownFile(contents, filename)

			if (groupSegment && groupSegment !== "ungrouped") {
				// Convert slug back to readable name: "my-group" -> "My Group"
				const readableGroup = groupSegment
					.replace(/-/g, " ")
					.replace(/\b\w/g, (c) => c.toUpperCase())
				article.groupName = article.groupName ?? readableGroup
			}

			articles.push(article)
		} catch (error) {
			console.error(`Error parsing markdown entry ${path}:`, error)
		}
	}

	return articles
}

async function parseMarkdownZip(file: File): Promise<MarkdownArticle[]> {
	const buffer = await file.arrayBuffer()
	const entries = unzipSync(new Uint8Array(buffer))

	return parseMarkdownEntries(
		Object.entries(entries)
			.filter(([, data]) => data.length > 0)
			.map(([path, data]) => ({ path, contents: strFromU8(data) })),
	)
}

/**
 * Gives each article its own glyph, keyed on position so two files that share
 * a name still get one icon each.
 */
function withUniqueIcons(articles: MarkdownArticle[]): MarkdownArticle[] {
	const icons = assignUniqueIcons(
		articles.map((article, index) => ({
			id: String(index),
			title: article.title,
		})),
		{ allowRepeats: true },
	)

	return articles.map((article, index) => ({
		...article,
		icon: icons[String(index)],
	}))
}

/**
 * Parse multiple markdown or zip files
 */
export async function parseMarkdownFiles(
	files: FileList,
): Promise<MarkdownArticle[]> {
	const articles: MarkdownArticle[] = []

	for (const file of Array.from(files)) {
		const name = file.name.toLowerCase()

		if (name.endsWith(".zip")) {
			try {
				const zipArticles = await parseMarkdownZip(file)
				articles.push(...zipArticles)
			} catch (error) {
				console.error(`Error parsing zip file ${file.name}:`, error)
			}
		} else if (name.endsWith(".md")) {
			try {
				const content = await file.text()
				const article = parseMarkdownFile(content, file.name)
				articles.push(article)
			} catch (error) {
				console.error(`Error parsing file ${file.name}:`, error)
			}
		}
	}

	return withUniqueIcons(articles)
}
